// AI Analysis Dashboard - Main Component
// Location: fongo-dashboard/src/components/AIAnalysis.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Call {
  call_id: string;
  customer_name: string;
  caller_name: string;
  channel_type: string;
  issue_category: string;
  resolution_status: string;
  duration_seconds: number;
  created_at: string;
  overall_grade: number;
  grade_letter: string;
  has_critical_issues: boolean;
  has_moderate_issues: boolean;
  requires_review: boolean;
  critical_count: number;
  moderate_count: number;
}

interface DashboardMetrics {
  overall: {
    total_calls: number;
    avg_grade: number;
    critical_count: number;
    moderate_count: number;
    perfect_count: number;
  };
  gradeDistribution: Array<{
    grade_letter: string;
    count: number;
  }>;
  topIssues: Array<{
    issue_type: string;
    issue_title: string;
    issue_category: string;
    occurrence_count: number;
    fix_status: string;
    customer_impact: string;
  }>;
}

export default function AIAnalysis() {
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState<Call[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [gradeFilter, setGradeFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [gradeFilter, channelFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [callsRes, metricsRes] = await Promise.all([
        fetch(`/api/ai-analysis/calls?grade=${gradeFilter}&channel=${channelFilter}&limit=50`),
        fetch('/api/ai-analysis/dashboard?period=7d')
      ]);

      if (callsRes.ok && metricsRes.ok) {
        const callsData = await callsRes.json();
        const metricsData = await metricsRes.json();
        setCalls(callsData.calls || []);
        setMetrics(metricsData);
      }
    } catch (error) {
      console.error('Error fetching AI analysis data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: string) => {
    if (!grade) return 'default';
    if (grade.startsWith('A')) return 'success';
    if (grade.startsWith('B')) return 'info';
    if (grade.startsWith('C')) return 'warning';
    return 'error';
  };

  const getIssueColor = (impact: string) => {
    if (impact === 'high') return 'error';
    if (impact === 'medium') return 'warning';
    return 'info';
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const renderSummaryCards = () => {
    if (!metrics) return null;

    const avgGrade = Number(metrics.overall.avg_grade) || 0;
    const gradePercent = avgGrade.toFixed(1);
    const gradeLetter = avgGrade >= 93 ? 'A' : avgGrade >= 85 ? 'A-' : avgGrade >= 80 ? 'B+' : avgGrade >= 75 ? 'B' : avgGrade >= 70 ? 'B-' : avgGrade >= 60 ? 'C' : 'D';

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Grade</Typography>
              <Typography variant="h2">{gradeLetter}</Typography>
              <Typography variant="body2">{gradePercent}%</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Calls</Typography>
              <Typography variant="h2">{metrics.overall.total_calls}</Typography>
              <Typography variant="body2">Last 7 days</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Critical Issues</Typography>
              <Typography variant="h2">{metrics.overall.critical_count || 0}</Typography>
              <Typography variant="body2">
                {metrics.overall.total_calls > 0 
                  ? ((Number(metrics.overall.critical_count) / Number(metrics.overall.total_calls)) * 100).toFixed(1)
                  : '0.0'}% of calls
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Perfect Handling</Typography>
              <Typography variant="h2">{metrics.overall.perfect_count || 0}</Typography>
              <Typography variant="body2">
                {metrics.overall.total_calls > 0
                  ? ((Number(metrics.overall.perfect_count) / Number(metrics.overall.total_calls)) * 100).toFixed(1)
                  : '0.0'}% of calls
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderCallsList = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Issue</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Issues</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.map((call) => (
              <TableRow 
                key={call.call_id}
                sx={{ 
                  backgroundColor: call.has_critical_issues ? '#ffebee' : 
                                  call.has_moderate_issues ? '#fff3e0' : 
                                  'inherit',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
                onClick={() => navigate(`/ai-analysis/call/${call.call_id}`)}
              >
                <TableCell>
                  {new Date(call.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {call.customer_name}
                  </Typography>
                  {call.caller_name && call.caller_name !== call.customer_name && (
                    <Typography variant="caption" color="text.secondary">
                      Caller: {call.caller_name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={call.channel_type} 
                    size="small" 
                    color={call.channel_type === 'VOICE' ? 'primary' : 'secondary'}
                  />
                </TableCell>
                <TableCell>{call.issue_category}</TableCell>
                <TableCell>{formatDuration(call.duration_seconds)}</TableCell>
                <TableCell>
                  <Chip 
                    label={call.grade_letter || 'N/A'} 
                    color={getGradeColor(call.grade_letter)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {call.critical_count > 0 && (
                    <Chip 
                      icon={<ErrorIcon />}
                      label={`${call.critical_count} Critical`}
                      color="error"
                      size="small"
                      sx={{ mr: 0.5 }}
                    />
                  )}
                  {call.moderate_count > 0 && (
                    <Chip 
                      icon={<WarningIcon />}
                      label={`${call.moderate_count} Moderate`}
                      color="warning"
                      size="small"
                    />
                  )}
                  {call.critical_count === 0 && call.moderate_count === 0 && (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="None"
                      color="success"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/ai-analysis/call/${call.call_id}`);
                    }}
                  >
                    View Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTopIssues = () => {
    if (!metrics || !metrics.topIssues) return null;

    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Top Issues Requiring Attention
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Issue</TableCell>
                <TableCell>Occurrences</TableCell>
                <TableCell>Impact</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.topIssues.map((issue, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {issue.issue_title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {issue.issue_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={issue.occurrence_count} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={issue.customer_impact} 
                      color={getIssueColor(issue.customer_impact)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={issue.fix_status.replace('_', ' ')} 
                      size="small"
                      color={issue.fix_status === 'fixed' ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <AssessmentIcon sx={{ fontSize: 40, verticalAlign: 'middle', mr: 1 }} />
            AI Performance Analysis & Quality Assurance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automated analysis and grading of every AI interaction
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/prompt-suggestions')}
          startIcon={<TrendingUpIcon />}
        >
          View Prompt Suggestions
        </Button>
      </Box>

      {renderSummaryCards()}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Grade Filter</InputLabel>
              <Select
                value={gradeFilter}
                label="Grade Filter"
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <MenuItem value="all">All Grades</MenuItem>
                <MenuItem value="critical">Critical Issues Only</MenuItem>
                <MenuItem value="needs_review">Needs Review</MenuItem>
                <MenuItem value="A+">A+</MenuItem>
                <MenuItem value="A">A</MenuItem>
                <MenuItem value="A-">A-</MenuItem>
                <MenuItem value="B+">B+</MenuItem>
                <MenuItem value="B">B</MenuItem>
                <MenuItem value="B-">B-</MenuItem>
                <MenuItem value="C">C</MenuItem>
                <MenuItem value="D">D</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Channel</InputLabel>
              <Select
                value={channelFilter}
                label="Channel"
                onChange={(e) => setChannelFilter(e.target.value)}
              >
                <MenuItem value="all">All Channels</MenuItem>
                <MenuItem value="voice">Voice Only</MenuItem>
                <MenuItem value="chat">Chat/SMS Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              onClick={fetchData}
              startIcon={<TrendingUpIcon />}
            >
              Refresh Data
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {renderCallsList()}
      
      {renderTopIssues()}
    </Container>
  );
}

